import { TimeString, TimeHHMM } from '../types/models'
import { DateTime } from 'luxon'
import { ScaleLabeling } from '../types/charts'
import { CITime } from '../types/testing'

/**
 * A collection of time related utilies.
 */

const MS_PER_DAY = 86400000

const COUNTRY_TIMEZONES: Record<string, string> = {
  FI: 'Europe/Helsinki',
  EE: 'Europe/Tallinn',
  SE: 'Europe/Stockholm',
  LV: 'Europe/Riga',
  LT: 'Europe/Vilnius',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  DE: 'Europe/Berlin',
}

const DEFAULT_TZ = 'Europe/Helsinki'

export function countryToTimezone(iso3166?: string): string {
  return (iso3166 && COUNTRY_TIMEZONES[iso3166]) || DEFAULT_TZ
}

/**
 * Subtract exact days from a UTC ISO string using millisecond arithmetic,
 * preserving the UTC time-of-day. Luxon's `minus({ days })` operates in
 * the local timezone and drifts by 1 hour when crossing a DST boundary.
 */
function utcMinusDays(iso: TimeString, days: number): TimeString {
  const ms = DateTime.fromISO(iso).toMillis() - days * MS_PER_DAY
  return DateTime.fromMillis(ms, { zone: 'utc' }).toISO() as TimeString
}

/**
 * Start of the day holding the given date, in the given timezone, as a UTC ISO
 * string.
 *
 * Returns null for a missing or invalid date. A date picker hands out an
 * *invalid* DateTime while a date is being typed or half-cleared - not null -
 * and luxon's toISO() then yields null, which a template string would happily
 * bake into a query as the literal "null". Callers must skip the request until
 * this returns a real timestamp.
 */
export function dayStartISO(date: DateTime | null, timezone = DEFAULT_TZ): TimeString | null {
  if (!date || !date.isValid) {
    return null
  }

  return date.setZone(timezone).startOf('day').toUTC().toISO() as TimeString | null
}

/**
 * Figure out first and last date as well as first recommended API fetching date for textual range.
 */
export function range(range: ScaleLabeling, oldestDate: TimeString | null = null, fetchRangeMultiplier = 10, timezone = DEFAULT_TZ): [TimeString, TimeString, TimeString] {

  let maxDate: TimeString
  let minDate: TimeString
  let fetchDate: TimeString

  switch (range) {
    case 'minute':
      maxDate = DateTime.local().startOf('minute').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ hours: 1 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ hours: 1 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case 'quarter':
      maxDate = DateTime.now().setZone(timezone).startOf('hour').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ hours: 24 + 1 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ hours: 24 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case '24h':
      maxDate = DateTime.local().startOf('hour').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ hours: 24 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ hours: 24 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case '7d':
      maxDate = DateTime.local().startOf('day').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ days: 7 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ days: 7 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case '30d':
      maxDate = DateTime.local().startOf('day').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ days: 30 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ days: 30 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case 'hour':
      maxDate = DateTime.now().setZone(timezone).startOf('hour').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ hours: 24 + 1 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ hours: 24 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case 'day':
      maxDate = DateTime.now().setZone(timezone).startOf('day').plus({ days: 1 }).toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ days: 12 + 1 }).toUTC().toISO() as TimeString
      fetchDate = utcMinusDays(maxDate, 12 * fetchRangeMultiplier)
      break
    case 'week':
      maxDate = DateTime.now().setZone(timezone).startOf('week').plus({ weeks: 1 }).toUTC().toISO() as TimeString
      minDate = utcMinusDays(maxDate, 11 * 7)
      fetchDate = utcMinusDays(maxDate, 11 * 7 * fetchRangeMultiplier)
      break
    case 'month':
      maxDate = DateTime.now().setZone(timezone).startOf('month').plus({ months: 1 }).toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(maxDate).minus({ months: 12 }).toUTC().toISO() as TimeString
      fetchDate = DateTime.fromISO(maxDate).minus({ months: 12 * fetchRangeMultiplier }).toUTC().toISO() as TimeString
      break
    case 'year':
      // Trailing 365 days, not calendar years. The window ends at the start of
      // the current hour, i.e. at the end of the last fully completed hour, so
      // that a half-finished hour never drags the figures around.
      maxDate = DateTime.now().setZone(timezone).startOf('hour').toUTC().toISO() as TimeString
      minDate = utcMinusDays(maxDate, 365)
      fetchDate = utcMinusDays(maxDate, 365 * fetchRangeMultiplier)
      break
    case 'all':
      maxDate = DateTime.now().setZone(timezone).startOf('day').toUTC().toISO() as TimeString
      minDate = DateTime.fromISO(oldestDate || maxDate).setZone(timezone).startOf('day').toUTC().toISO() as TimeString
      return [minDate, maxDate, minDate]
    default:
      throw new Error(`Invalid time range '${range}'.`)
  }

  // The visible window is always returned as asked for, even when the port has
  // only been recording for part of it: a device installed yesterday shows
  // yesterday's kWh on the year view rather than an empty one. Only the
  // fetching start is clamped, to avoid asking the API for a period that is
  // known to hold no data at all.
  if (oldestDate && oldestDate > fetchDate) {
    return [minDate, maxDate, minDate]
  }

  return [minDate, maxDate, fetchDate]
}

/**
 * Resolve a visual timestamp pair to either zero, one or two timestamps.
 */
export function visual2intervals(start: TimeHHMM, stop: TimeHHMM): [TimeHHMM, TimeHHMM][] {
  if (start === stop) {
    return []
  }
  if (stop === '00:00') {
    return [[start, '24:00' as TimeHHMM]]
  }
  if (stop < start) {
    return [['00:00' as TimeHHMM, stop], [start, '24:00' as TimeHHMM]]
  }
  return [[start, stop]]
}

/**
 * Sort a list of timestamps.
 */
export function timeSort(ranges: [TimeHHMM, TimeHHMM][]): [TimeHHMM, TimeHHMM][] {
  return ranges.sort((a, b) => (a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : (a[1] < b[1] ? -1 : (a[1] > b[1] ? 1 : 0)))))
}

/**
 * Get later time.
 */
export function timeMax(a: TimeHHMM, b: TimeHHMM): TimeHHMM {
  return a > b ? a : b
}

/**
 * Merge all overlapping time ranges.
 */
export function rangeMerge(ranges: [TimeHHMM, TimeHHMM][]) {

  const joinOverlaps = (prev: [TimeHHMM, TimeHHMM][], cur: [TimeHHMM, TimeHHMM]): [TimeHHMM, TimeHHMM][] => {
    if (prev.length > 0) {
      const last = prev[prev.length - 1]
      if (last[1] >= cur[0]) {
        last[1] = timeMax(last[1], cur[1])
        return prev
      }
    }

    if (cur[0] === cur[1]) {
      return prev
    }
    return prev.concat([cur])
  }

  return timeSort(ranges).reduce((prev: [TimeHHMM, TimeHHMM][], cur) => joinOverlaps(prev, cur), [])
}

/**
 * Get the current time taking possible CI clock into account.
 */
export function now(): DateTime {
  const { enabled, base, changed, speed } = getCITime()
  const nowStamp = DateTime.now().toMillis()
  return enabled ? DateTime.fromMillis(base + (nowStamp - changed) * speed) : DateTime.now()
}

/**
 * UTC version of the current time.
 */
export function nowutc(): DateTime {
  return now().toUTC()
}

/**
 * Turn on or off CI timing.
 */
function setCITime(ciTime: CITime | null) {
  if (ciTime === null) {
    localStorage.removeItem('ci')
  } else {
    localStorage.setItem('ci', JSON.stringify(ciTime))
  }
}

/**
 * Get the CI timing steup.
 */
function getCITime() {
  const ci = localStorage.getItem('ci') || '{"enabled": false, "base": null, "changed": null, "speed": 1}'
  return JSON.parse(ci)
}

/**
 * Set the base point for CI timing and enable it.
 */
export function setCIBaseTime(stamp: DateTime | null) {
  if (stamp === null) {
    setCITime(null)
  } else {
    setCITime({ ...getCITime(), changed: DateTime.now().toMillis(), enabled: true, base: stamp.toMillis() })
  }
}

/**
 * Set the clock speed multiplier for CI.
 */
export function setCISpeed(speed: number) {
  setCITime({ ...getCITime(), speed })
}

/**
 * Check if CI time is enabled.
 */
export function hasCIEnabled(): boolean {
  return getCITime().enabled
}

/**
 * Create promise that resolves after delay.
 * @param ms
 */
export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
